import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const BillyMonitorScreen = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || '';

  const fetchMonitorData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/billy/monitor`, {
        credentials: 'include'
      });

      if (response.status === 401) {
        // Try to refresh the token before logging out
        const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (refreshRes.ok) {
          // Retry the original request with the new token
          const retryResponse = await fetch(`${API_URL}/api/billy/monitor`, {
            credentials: 'include'
          });
          if (retryResponse.ok) {
            const result = await retryResponse.json();
            setData(result);
            return;
          }
        }

        // Refresh failed - actually logged out
        logout();
        return;
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching monitor data:', err);
    }
  }, [API_URL, logout]);

  // Set page title
  useEffect(() => {
    document.title = 'Days in the Life of Mz. Porter | Monitor';
  }, []);

  // Fetch monitor data every 3 seconds
  useEffect(() => {
    fetchMonitorData();
    const interval = setInterval(fetchMonitorData, 3000);
    return () => clearInterval(interval);
  }, [fetchMonitorData]);

  const handleStop = async () => {
    if (!window.confirm('Are you sure you want to stop the stream?')) {
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/billy/stop`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.status === 401) {
        const refreshRes = await fetch(`${API_URL}/api/auth/refresh`, {
          method: 'POST',
          credentials: 'include'
        });

        if (refreshRes.ok) {
          await fetch(`${API_URL}/api/billy/stop`, {
            method: 'POST',
            credentials: 'include'
          });
        } else {
          logout();
          return;
        }
      }

      // Refresh data
      await fetchMonitorData();
    } catch (err) {
      console.error('Error stopping stream:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  if (!data) {
    return (
      <div style={styles.container}>
        <div style={styles.loading}>Loading...</div>
      </div>
    );
  }

  const isLive = data.isLivestreaming;

  return (
    <div style={styles.container}>
      {/* User Info Bar */}
      <div style={styles.userBar} className="user-bar">
        <span style={styles.userEmail}>{user?.email}</span>
        <button onClick={handleLogout} style={styles.logoutButton}>
          Sign out
        </button>
      </div>

      <div style={styles.content}>
        {/* Header */}
        <div style={styles.header}>
          <h1 style={styles.title}>Days in the Life of Mz. Porter</h1>
          <div style={styles.statusBadge}>
            <div style={{
              ...styles.statusDot,
              backgroundColor: isLive ? '#4CAF50' : '#f44336'
            }} />
            <span>{data.streamStatus}</span>
          </div>
          <button
            onClick={() => navigate('/billy/start')}
            style={styles.startButton}
          >
            Start
          </button>
        </div>

        {/* Main Stats */}
        <div style={styles.statsGrid}>
          {/* Day Counter */}
          <div style={styles.statCard}>
            <span style={styles.statText}>Day {data.dayOfShow} of {data.totalDays}</span>
          </div>

          {/* Viewer Count */}
          <div style={styles.statCard}>
            <span style={styles.statText}>{data.viewersFormatted} Watching now</span>
          </div>
        </div>

        {/* Viewer Preview */}
        <div style={styles.viewerPreview}>
          <h3 style={styles.previewTitle}>What viewers see</h3>
          <div style={styles.previewContainer}>
            <iframe
              src="https://player.castr.com/live_676aa5b0ea6e11f088b75debb5eb63f6?paywall=1&rand=20yrpli&guestLink=1"
              style={styles.previewIframe}
              title="What viewers see"
            />
          </div>
        </div>

        {/* Detailed Info */}
        <div style={styles.infoSection}>
          <div style={styles.infoRow}>
            <span style={styles.infoLabel}>Launch Date:</span>
            <span style={styles.infoValue}>
              {new Date(data.launchDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </span>
          </div>
        </div>

        {/* Actions */}
        <div style={styles.actions}>
          {isLive && (
            <button
              onClick={handleStop}
              disabled={loading}
              style={{
                ...styles.stopButton,
                opacity: loading ? 0.6 : 1
              }}
            >
              {loading ? 'STOPPING...' : 'STOP STREAM'}
            </button>
          )}

        </div>

        {/* Auto-refresh indicator */}
        <div style={styles.footer}>
          <p style={styles.footerText}>
            Auto-refreshing every 3 seconds
          </p>
          <p style={styles.footerText}>
            Last updated: {new Date(data.currentTime).toLocaleTimeString()}
          </p>
        </div>
      </div>

      <style>{`
        @media (max-width: 768px) {
          .user-bar {
            position: relative !important;
            top: auto !important;
            right: auto !important;
            left: 0 !important;
            margin-bottom: 10px !important;
            justify-content: space-between !important;
          }
        }
      `}</style>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    padding: '20px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    position: 'relative'
  },
  userBar: {
    position: 'absolute',
    top: '20px',
    right: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '15px'
  },
  userEmail: {
    color: '#888',
    fontSize: '14px'
  },
  userRole: {
    color: '#4CAF50',
    fontSize: '12px',
    backgroundColor: '#2a2a2a',
    padding: '4px 8px',
    borderRadius: '4px',
    textTransform: 'uppercase'
  },
  logoutButton: {
    backgroundColor: 'transparent',
    color: '#888',
    border: '1px solid #3a3a3a',
    borderRadius: '6px',
    padding: '8px 16px',
    fontSize: '14px',
    cursor: 'pointer',
    transition: 'all 0.2s'
  },
  content: {
    maxWidth: '1000px',
    margin: '0 auto',
    paddingTop: '40px'
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '40px',
    flexWrap: 'wrap',
    gap: '20px'
  },
  title: {
    fontSize: '32px',
    fontWeight: 'bold',
    margin: '0'
  },
  statusBadge: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    backgroundColor: '#2a2a2a',
    padding: '10px 20px',
    borderRadius: '8px',
    fontSize: '16px',
    textTransform: 'uppercase'
  },
  statusDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%'
  },
  startButton: {
    backgroundColor: '#4CAF50',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '10px 20px',
    fontSize: '14px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '20px',
    marginBottom: '30px'
  },
  statCardLarge: {
    backgroundColor: '#2a2a2a',
    padding: '30px',
    borderRadius: '12px',
    textAlign: 'center',
    gridColumn: 'span 1'
  },
  statCard: {
    backgroundColor: '#2a2a2a',
    padding: '20px',
    borderRadius: '12px',
    textAlign: 'center',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  statText: {
    fontSize: '18px',
    fontWeight: '500'
  },
  dayCounterLarge: {
    fontSize: '72px',
    fontWeight: 'bold',
    lineHeight: '1',
    marginBottom: '10px',
    color: '#4CAF50'
  },
  dayCounterLabel: {
    fontSize: '36px',
    fontWeight: 'bold',
    color: '#4CAF50'
  },
  statValue: {
    fontSize: '48px',
    fontWeight: 'bold',
    marginBottom: '10px',
    lineHeight: '1'
  },
  statLabel: {
    fontSize: '16px',
    color: '#888',
    textTransform: 'uppercase'
  },
  infoSection: {
    backgroundColor: '#2a2a2a',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '30px'
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '12px 0'
  },
  infoLabel: {
    color: '#888',
    fontSize: '14px'
  },
  infoValue: {
    fontSize: '14px',
    fontWeight: '500',
    textAlign: 'right'
  },
  actions: {
    display: 'flex',
    gap: '15px',
    flexWrap: 'wrap',
    marginBottom: '30px'
  },
  stopButton: {
    backgroundColor: '#f44336',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  backButton: {
    backgroundColor: '#555',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '14px 32px',
    fontSize: '16px',
    cursor: 'pointer',
    transition: 'background-color 0.2s'
  },
  viewerPreview: {
    backgroundColor: '#2a2a2a',
    padding: '20px',
    borderRadius: '12px',
    marginBottom: '30px'
  },
  previewTitle: {
    fontSize: '16px',
    fontWeight: '600',
    margin: '0 0 15px 0',
    color: '#888'
  },
  previewContainer: {
    position: 'relative',
    width: '100%',
    paddingBottom: '56.25%',
    backgroundColor: '#000',
    borderRadius: '8px',
    overflow: 'hidden'
  },
  previewIframe: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    border: 'none'
  },
  footer: {
    textAlign: 'center',
    paddingTop: '20px',
    borderTop: '1px solid #3a3a3a'
  },
  footerText: {
    color: '#666',
    fontSize: '12px',
    margin: '5px 0'
  },
  loading: {
    fontSize: '24px',
    color: '#888',
    textAlign: 'center',
    paddingTop: '100px'
  }
};

export default BillyMonitorScreen;
